import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ProductCard } from "@/components/ProductCard";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { CategorySquares } from "@/components/CategorySquares";
import { ReferralCta } from "@/components/ReferralCta";
import { RecentlySold } from "@/components/RecentlySold";
import { Tag, ShieldCheck, CreditCard, ChevronRight, Truck, MessageCircle, Sparkles } from "lucide-react";
import { trackPageView } from "@/utils/analytics";
import { captureReferral } from "@/utils/referral";
import { openWhatsApp } from "@/utils/whatsapp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    captureReferral();
    trackPageView("home");
  }, []);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch {
        toast.error("No se pudieron cargar los productos");
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, []);

  const storefront = useMemo(() => products.filter(p => p.category !== "ofertas"), [products]);


  return (
    <div className="min-h-screen bg-[#050505]" data-testid="home-page">
      <Navbar />

      {/* Hero Section */}
      <section
        className="relative min-h-[70vh] flex flex-col justify-center items-center text-center px-4 overflow-hidden"
        data-testid="hero-section"
        style={{
          background: "linear-gradient(135deg, rgba(176,38,255,0.25) 0%, rgba(5,5,5,1) 70%)"
        }}
      >
        {/* Glow orbs */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#B026FF]/20 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-[#00FF66]/10 rounded-full blur-[80px] pointer-events-none" />

        <h1 className="font-racing text-4xl sm:text-5xl lg:text-6xl text-white mb-4 animate-fade-in" data-testid="hero-title">
          Ropa Original. Precios Inteligentes.
        </h1>
        <p className="font-manrope text-base sm:text-lg text-[#A0A0A0] max-w-lg mx-auto mb-8 animate-fade-in-delay">
          Encuentra marcas autenticas sin pagar de mas.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 animate-fade-in-delay-2">
          <a
            href="#categorias"
            className="inline-flex items-center gap-2 bg-[#00FF66] text-[#050505] px-8 py-3 rounded-full font-semibold text-sm uppercase tracking-wide hover:shadow-[0_0_20px_rgba(0,255,102,0.4)] transition-all duration-300 hover:-translate-y-0.5"
            data-testid="cta-shop"
          >
            Comprar Ahora
            <ChevronRight className="w-4 h-4" />
          </a>
          <a
            href="#ofertas"
            className="inline-flex items-center gap-2 border border-[#B026FF] text-[#B026FF] px-8 py-3 rounded-full font-semibold text-sm uppercase tracking-wide hover:bg-[#B026FF] hover:text-white hover:shadow-[0_0_20px_rgba(176,38,255,0.4)] transition-all duration-300 hover:-translate-y-0.5"
            data-testid="cta-offers"
          >
            Ver Ofertas
          </a>
        </div>
      </section>

      {/* Category Squares */}
      <section id="categorias" className="py-12 sm:py-16 px-4" data-testid="categories-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-oswald text-2xl sm:text-3xl font-bold text-white text-center mb-3 uppercase tracking-wider">
            Que buscas?
          </h2>
          <p className="text-[#A0A0A0] text-base text-center mb-8">Toca una opcion para ver los productos</p>
          <CategorySquares />
        </div>
      </section>

      {/* Ofertas Section */}
      <section id="ofertas" className="py-12 sm:py-16 px-4" data-testid="deals-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-oswald text-2xl sm:text-3xl font-bold text-white text-center mb-10 uppercase tracking-wider">
            Ofertas
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Shipping note */}
            <div className="glass-card p-6 text-center group border-[#25D366]/40 hover:shadow-[0_0_24px_rgba(37,211,102,0.25)] transition-all duration-300" data-testid="deal-shipping">
              <div className="w-12 h-12 rounded-full bg-[#25D366]/10 flex items-center justify-center mx-auto mb-4">
                <Truck className="w-6 h-6 text-[#25D366]" />
              </div>
              <h3 className="font-oswald text-2xl font-bold text-white mb-2">Envios</h3>
              <p className="text-[#A0A0A0] text-sm mb-4">Si quieres que te enviemos algo, mandanos un WhatsApp.</p>
              <button
                onClick={() => openWhatsApp("Hola! Me gustaria que me enviaran un producto. Me pueden dar informacion sobre envios?")}
                className="inline-flex items-center gap-2 bg-[#25D366] text-white px-5 py-2.5 rounded-full text-sm font-bold hover:shadow-[0_0_16px_rgba(37,211,102,0.4)] transition-all"
                data-testid="deal-shipping-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4" /> Enviar WhatsApp
              </button>
            </div>
            {/* Deal 1 */}
            <div className="glass-card p-6 text-center group hover:shadow-[0_0_24px_rgba(0,255,102,0.2)] transition-all duration-300" data-testid="deal-hats">
              <div className="w-12 h-12 rounded-full bg-[#00FF66]/10 flex items-center justify-center mx-auto mb-4">
                <Tag className="w-6 h-6 text-[#00FF66]" />
              </div>
              <h3 className="font-oswald text-2xl font-bold text-white mb-2">2 x $450</h3>
              <p className="text-[#A0A0A0] text-sm">Lleva 2 gorras por solo $450 cada una</p>
            </div>

            {/* Deal 2 */}
            <div className="glass-card p-6 text-center group hover:shadow-[0_0_24px_rgba(176,38,255,0.2)] transition-all duration-300" data-testid="deal-discount">
              <div className="w-12 h-12 rounded-full bg-[#B026FF]/10 flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-6 h-6 text-[#B026FF]" />
              </div>
              <h3 className="font-oswald text-2xl font-bold text-white mb-3">Compra mas, ahorra mas</h3>
              <div className="space-y-2 text-sm">
                <p className="text-[#A0A0A0]">2 prendas = <span className="text-[#00FF66] font-bold">$100 OFF</span></p>
                <p className="text-[#A0A0A0]">3 prendas = <span className="text-[#00FF66] font-bold">$150 OFF</span></p>
                <p className="text-[#A0A0A0]">4 prendas = <span className="text-[#00FF66] font-bold">$200 OFF</span></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payment Plans */}
      <section className="py-12 sm:py-16 px-4 border-y border-white/5" data-testid="payment-program-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="w-12 h-12 rounded-full bg-[#B026FF]/10 flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-6 h-6 text-[#B026FF]" />
            </div>
            <h2 className="font-oswald text-2xl sm:text-3xl font-bold text-white uppercase">
              Planes de Pago
            </h2>
            <p className="text-[#A0A0A0] text-base mt-2">
              Aparta lo que te gusta hoy. Tu decides como pagar.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 2-Payment Plan */}
            <div className="glass-card p-6" data-testid="plan-2-payments">
              <h3 className="font-oswald text-xl font-bold text-[#00FF66] mb-1 uppercase">2 Pagos</h3>
              <p className="text-sm text-[#A0A0A0] mb-5">Paga la mitad hoy, la otra mitad al recoger.</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-[#050505] rounded-xl p-3 text-center border border-white/5">
                  <p className="font-oswald text-2xl font-bold text-[#00FF66]">50%</p>
                  <p className="text-xs text-[#A0A0A0] mt-1">Hoy</p>
                </div>
                <div className="bg-[#050505] rounded-xl p-3 text-center border border-white/5">
                  <p className="font-oswald text-2xl font-bold text-white">2 sem</p>
                  <p className="text-xs text-[#A0A0A0] mt-1">Plazo</p>
                </div>
                <div className="bg-[#050505] rounded-xl p-3 text-center border border-white/5">
                  <p className="font-oswald text-2xl font-bold text-[#B026FF]">50%</p>
                  <p className="text-xs text-[#A0A0A0] mt-1">Al recoger</p>
                </div>
              </div>
            </div>

            {/* 3-Payment Plan */}
            <div className="glass-card p-6" data-testid="plan-3-payments">
              <h3 className="font-oswald text-xl font-bold text-[#B026FF] mb-1 uppercase">3 Pagos</h3>
              <p className="text-sm text-[#A0A0A0] mb-5">Divide tu compra en 3 pagos iguales.</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-[#050505] rounded-xl p-3 text-center border border-white/5">
                  <p className="font-oswald text-2xl font-bold text-[#00FF66]">33%</p>
                  <p className="text-xs text-[#A0A0A0] mt-1">Hoy</p>
                </div>
                <div className="bg-[#050505] rounded-xl p-3 text-center border border-white/5">
                  <p className="font-oswald text-2xl font-bold text-white">33%</p>
                  <p className="text-xs text-[#A0A0A0] mt-1">2 semanas</p>
                </div>
                <div className="bg-[#050505] rounded-xl p-3 text-center border border-white/5">
                  <p className="font-oswald text-2xl font-bold text-[#B026FF]">33%</p>
                  <p className="text-xs text-[#A0A0A0] mt-1">4 semanas</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section id="productos" className="py-12 sm:py-16 px-4 bg-[#0a0a0a]" data-testid="products-section">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between mb-8 gap-4">
            <div>
              <span className="inline-flex items-center gap-2 bg-[#00FF66]/10 text-[#00FF66] text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider mb-3">
                <Sparkles className="w-3.5 h-3.5" /> Recien llegado
              </span>
              <h2 className="font-oswald text-2xl sm:text-3xl font-bold text-white uppercase tracking-wider">Productos</h2>
              <p className="text-[#A0A0A0] text-base mt-1">Lo mas nuevo en la tienda. Ropa original, precio justo.</p>
            </div>
            <Link to="/productos" className="hidden sm:inline-flex items-center gap-1 text-[#00FF66] text-sm font-semibold hover:underline whitespace-nowrap" data-testid="products-view-all">
              Ver todo <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-10 h-10 border-4 border-[#00FF66] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : storefront.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-oswald text-2xl text-[#A0A0A0] mb-2">Proximamente</p>
              <p className="text-[#A0A0A0]/60 text-sm">Nuevos productos en camino</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {storefront.slice(0, 8).map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
              <div className="text-center mt-10">
                <Link to="/productos" className="inline-flex items-center gap-2 bg-[#00FF66] text-[#050505] px-8 py-3.5 font-bold text-base uppercase tracking-wide rounded-full hover:shadow-[0_0_20px_rgba(0,255,102,0.4)] hover:-translate-y-0.5 transition-all duration-300" data-testid="products-view-more">
                  Ver todo ({storefront.length}) <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      <RecentlySold />

      <ReferralCta />

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
